/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import { IService } from 'generated-types';
import {
    IVsLogsParams,
    IVsLogsStateParams,
    TLogIdentifier,
    TVsLog,
    TVsLogsConstantParams,
    VsAppProfileType,
    VsLogsType,
} from '../vs-logs.types';
import { LOGS_API_URL } from '../services/vs-logs-api-request.service';
import { TVsConfig } from '../services/vs-logs.store';
import { convertQueryStringToApiString } from './vs-logs-filters.utils';

/**
 * Get relevant properties from a virtual service item.
 * TODO: change "any" to the VirtualService class once it's been converted to Typescript.
 */
export const getVsConfig = (virtualService: any): TVsConfig => {
    let vsAppProfileType = virtualService.appType();

    // type is set to 1 for L7 and 0 for L4 and DNS
    const vsLogsType = vsAppProfileType === VsAppProfileType.L7 ?
        VsLogsType.APPLICATION :
        VsLogsType.CONNECTION;

    const hasOverrideL4AppProfile = virtualService.getConfig().services.some(
        (service: IService) => service.override_application_profile_ref,
    );

    let nonSignificantLogsEnabled = true;
    const { analytics_policy: analyticsPolicy } = virtualService.getConfig();

    if (analyticsPolicy && analyticsPolicy.full_client_logs) {
        nonSignificantLogsEnabled = analyticsPolicy.full_client_logs.enabled;
    }

    // Treat any vsAppProfileType that is not L7 or DNS as L4 for the purpose of showing different sections
    if (vsAppProfileType !== VsAppProfileType.L7 && vsAppProfileType !== VsAppProfileType.DNS) {
        vsAppProfileType = VsAppProfileType.L4;
    }

    const vsConfig: TVsConfig = {
        vsName: virtualService.getName(),
        vsUuid: virtualService.getConfig().uuid,
        vsAppProfileType,
        vsLogsType,
        hasOverrideL4AppProfile,
        nonSignificantLogsEnabled,
        hasWafPolicy: virtualService.hasWAFPolicy(),
        wafPolicyRef: virtualService.getWafPolicyRef(),
        hasTcpProxyNetProfile: virtualService.hasTCPProxyNetProfile(),
    };

    return vsConfig;
};

/**
 * Convert state params to full set of API params needed for request.
 */
export const convertStateToApiParams = (
    stateParams: IVsLogsStateParams,
    constantParams: TVsLogsConstantParams,
): IVsLogsParams => {
    const { filters, ...remParams } = stateParams;

    const filterApiStrings: string[] = [];

    if (filters) {
        filters.forEach(filter => {
            filterApiStrings.push(convertQueryStringToApiString(filter, remParams.type));
        });
    }

    const fullParams: IVsLogsParams = {
        ...constantParams,
        ...remParams,
        filter: filterApiStrings,
    };

    return fullParams;
};

/**
 * Build a url from paramaters object.
 */
export const buildUrlFromParams = (params: IVsLogsParams): string => {
    let url = `${LOGS_API_URL}?`;

    for (const param in params) {
        if (Object.prototype.hasOwnProperty.call(params, param)) {
            const value = params[param];

            if (Array.isArray(value)) {
                url += value
                    .map(val => `${param}=${val}&`)
                    .join('');
            } else {
                url += `${param}=${value}&`;
            }
        }
    }

    return url;
};

/**
 * Needed since "isEmpty" from Underscore only detects emptiness for an object when there's no key.
 */
export const isObjectEmpty = (obj: Record<string, any>): boolean => {
    if (!obj || Object.keys(obj).length === 0) {
        return true;
    }

    return Object.keys(obj).every(key => obj[key] === null || obj[key] === undefined);
};

/**
 * Get the unique identifier of a log, calculated by log_id, service_engine, and vcpu_id.
 */
export const getLogIdentifier = (log: TVsLog): TLogIdentifier => {
    const {
        log_id: logId,
        service_engine: serviceEngine,
        vcpu_id: vcpuId,
    } = log;

    return `${logId}+${serviceEngine}+${vcpuId}`;
};

/**
 * Retrieve the three parts of a log identifier.
 */
export const retrieveLogIdentifierParts = (logIdentifier: TLogIdentifier): {
    logId: number;
    serviceEngine: string;
    vcpuId: number;
} => {
    const [logId, serviceEngine, vcpuId] = logIdentifier.split('+');

    return {
        logId: Number(logId),
        serviceEngine,
        vcpuId: Number(vcpuId),
    };
};
